import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { SubpageFoodCostHeader } from '@/components/food-cost/SubpageFoodCostHeader'
import {
    authorLocaleFreePath,
    getAuthorByLocaleSlug,
    listAuthorArticles,
    localizeAuthor,
} from '@/features/authors/authors'
import { LocaleLink } from '@/navigation/LocaleLink'
import { LocaleSeoLinks } from '@/navigation/LocaleSeoLinks'
import { SITE_ORIGIN } from '@/navigation/constants'
import { absoluteLocalizedUrl } from '@/navigation/locale'
import { useLandingLocale } from '@/navigation/useLandingLocale'
import { Footer } from '@/shared/ui/Footer'
import { PageHelmet } from '@/shared/ui/PageHelmet'
import '@/styles/food-cost-fonts.css'

import { NotFoundPage } from './NotFoundPage'

function formatPublishedAt(isoDate: string, locale: string) {
    return new Intl.DateTimeFormat(locale.startsWith('en') ? 'en-GB' : 'es-ES', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    }).format(new Date(`${isoDate}T12:00:00`))
}

export function AuthorPage() {
    const { slug = '' } = useParams<{ slug: string }>()
    const { t, i18n } = useTranslation()
    const locale = useLandingLocale()

    const definition = getAuthorByLocaleSlug(locale, slug)
    if (!definition) {
        return <NotFoundPage />
    }

    const author = localizeAuthor(definition, locale)
    const articles = listAuthorArticles(definition.name, locale)
    const authorPath = authorLocaleFreePath(definition)
    const canonicalUrl = absoluteLocalizedUrl(authorPath, locale)
    const title = t('authors.meta.title', '{{name}} | Tipi', { name: author.name })
    const description =
        author.bioParagraphs.find(Boolean) || t('authors.meta.descriptionFallback', 'Perfil del equipo Tipi.')

    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'Person',
        name: author.name,
        jobTitle: author.role,
        description,
        url: canonicalUrl,
        image: `${SITE_ORIGIN}${author.portraitSrc}`,
        worksFor: {
            '@type': 'Organization',
            name: 'Tipi',
            url: SITE_ORIGIN,
        },
    }

    return (
        <div className="flex min-h-screen flex-col bg-[#F5EEE4] font-['Archivo',system-ui,sans-serif] text-[#17130E] antialiased">
            <LocaleSeoLinks path={authorPath} locale={locale} />
            <PageHelmet locale={locale}>
                <title>{title}</title>
                <meta name='description' content={description} />
                <meta property='og:type' content='profile' />
                <meta property='og:title' content={title} />
                <meta property='og:description' content={description} />
                <meta property='og:image' content={`${SITE_ORIGIN}${author.portraitSrc}`} />
                <link
                    rel='preload'
                    href='/fonts/Archivo/archivo-latin.woff2'
                    as='font'
                    type='font/woff2'
                    crossOrigin='anonymous'
                />
                <script type='application/ld+json'>{JSON.stringify(jsonLd)}</script>
            </PageHelmet>

            <SubpageFoodCostHeader />

            <main className='mx-auto w-full max-w-[720px] flex-1 px-[22px] pb-12 pt-24 md:px-10 md:pb-16 md:pt-28'>
                <header className='mb-10 flex flex-col items-start gap-5 sm:flex-row sm:items-center sm:gap-7'>
                    <img
                        src={author.portraitSrc}
                        alt={author.name}
                        width={128}
                        height={128}
                        className='h-28 w-28 shrink-0 rounded-full object-cover sm:h-32 sm:w-32'
                        decoding='async'
                    />
                    <div className='flex flex-col gap-2.5'>
                        <div className='text-xs font-semibold uppercase tracking-[2px] text-[#F4502A]'>
                            {t('authors.eyebrow', 'Equipo Tipi')}
                        </div>
                        <h1 className='m-0 text-[32px] font-medium leading-[1.15] tracking-[-1px] text-[#17130E] md:text-[46px]'>
                            {author.name}
                        </h1>
                        <p className='m-0 text-base leading-relaxed text-[#5C5548]'>{author.role}</p>
                    </div>
                </header>

                <div className='mb-12 flex flex-col gap-5'>
                    {author.bioParagraphs.filter(Boolean).map((paragraph, index) => (
                        <p key={index} className='m-0 text-base leading-[1.7] text-[#17130E]'>
                            {paragraph}
                        </p>
                    ))}
                </div>

                {articles.length > 0 ? (
                    <section aria-labelledby='author-articles-heading'>
                        <h2
                            id='author-articles-heading'
                            className='mb-4 mt-0 text-xs font-semibold uppercase tracking-[2px] text-[#8A7F6F]'
                        >
                            {t('authors.articlesHeading', 'Artículos escritos')}
                        </h2>
                        <ul className='m-0 list-none p-0'>
                            {articles.map(article => (
                                <li key={article.id} className='border-t border-[#D8CFC0]'>
                                    <LocaleLink
                                        to={article.path}
                                        className='group flex flex-col gap-2 py-5 text-[#17130E] no-underline transition-colors hover:text-[#F4502A]'
                                    >
                                        <div className='flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8A7F6F]'>
                                            <span>
                                                {article.section === 'aprender'
                                                    ? t('authors.section.learn', 'Aprender')
                                                    : t('authors.section.news', 'Actualidad hostelera')}
                                            </span>
                                            <time dateTime={article.publishedAt}>
                                                {formatPublishedAt(article.publishedAt, i18n.language)}
                                            </time>
                                        </div>
                                        <h3 className='m-0 text-base font-semibold leading-snug md:text-[17px]'>
                                            {article.title}
                                        </h3>
                                        <p className='m-0 max-w-[52ch] text-[15px] leading-[1.65] text-[#5C5548]'>
                                            {article.description}
                                        </p>
                                    </LocaleLink>
                                </li>
                            ))}
                        </ul>
                    </section>
                ) : null}
            </main>

            <Footer landingPage='author' className='border-[#F5EEE4]/10 bg-[#17130E]' />
        </div>
    )
}
